import { dbManager } from './db_manager';
import { supabase } from './supabaseClient';

export function extractUniqueWords(textOrArray: string | string[] | any[]): string[] {
  let combinedText = '';
  if (Array.isArray(textOrArray)) {
    combinedText = textOrArray
      .map(item => {
        if (typeof item === 'string') return item;
        return item?.original || item?.phrase || item?.text || item?.word || item?.translation || '';
      })
      .join(' ');
  } else if (typeof textOrArray === 'string') {
    combinedText = textOrArray;
  }

  if (!combinedText) return [];

  // Match all words including Unicode letters (Portuguese, Spanish, French, German, Russian, etc.)
  const matches = combinedText.match(/\p{L}+/gu);
  if (!matches) return [];

  const cleanWords = new Set<string>();
  for (const match of matches) {
    const word = match.toLowerCase().trim();
    if (word.length >= 2) {
      cleanWords.add(word);
    }
  }

  return Array.from(cleanWords);
}

export const uniqueWordsService = {
  extractUniqueWords,

  /**
   * Event-Driven update: Calls backend to process exercise completion,
   * updates completed_ids_json (structured by type: flashcards, writing, pronunciation, quizzes, roadmap),
   * deduplicates words, and updates total_unique_words in Cloudflare D1 logs_unique_words table.
   */
  async recordExerciseCompletion(
    userId: string | undefined,
    language: string,
    exerciseType: 'flashcards' | 'writing' | 'pronunciation' | 'quizzes' | 'roadmap' | string,
    exerciseId: string | number,
    textOrWords?: string | string[] | any[],
    r2Url?: string
  ): Promise<number> {
    if (!language) language = 'en';
    const langCode = language.toLowerCase();
    const type = (exerciseType || 'general').toLowerCase();

    const localCacheKey = `libritui_unique_words_${userId || 'guest'}_${langCode}`;

    if (!userId || userId === 'guest' || userId === 'undefined') {
      // Local fallback for guest mode
      try {
        const words = extractUniqueWords(textOrWords);
        const stored = localStorage.getItem(localCacheKey);
        const data = stored ? JSON.parse(stored) : { flashcards: [], writing: [], pronunciation: [], quizzes: [], roadmap: [], _words: [] };
        if (!Array.isArray(data[type])) data[type] = [];
        if (!data[type].includes(exerciseId)) {
          data[type].push(exerciseId);
          const wordsSet = new Set([...(data._words || []), ...words]);
          data._words = Array.from(wordsSet);
          localStorage.setItem(localCacheKey, JSON.stringify(data));
          return wordsSet.size;
        }
        return (data._words || []).length;
      } catch {
        return 0;
      }
    }

    try {
      const extractedText = typeof textOrWords === 'string'
        ? textOrWords
        : extractUniqueWords(textOrWords).join(' ');

      const payload = {
        action: 'record_unique_words',
        user_id: userId,
        language: langCode,
        exercise_type: type,
        exercise_id: exerciseId,
        text: extractedText,
        r2_url: r2Url
      };

      // 1. Try direct Cloudflare Pages Function endpoint
      let backendSuccess = false;
      let newTotal = 0;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch('/api/complete-exercise', {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const resJson = await response.json();
          if (resJson && resJson.success) {
            backendSuccess = true;
            newTotal = resJson.total_unique_words || 0;
            localStorage.setItem(localCacheKey, JSON.stringify(resJson));
          }
        }
      } catch (endpointErr) {
        console.warn('[uniqueWordsService] Direct endpoint fetch error, fallback to dbManager:', endpointErr);
      }

      // 2. Fallback to dbManager customAction if direct endpoint didn't respond
      if (!backendSuccess) {
        const dbRes: any = await dbManager.execute({
          action: 'record_unique_words' as any,
          table: 'logs_unique_words',
          data: payload,
          user_id: userId
        });

        if (dbRes && dbRes.success) {
          backendSuccess = true;
          newTotal = dbRes.total_unique_words || 0;
          localStorage.setItem(localCacheKey, JSON.stringify(dbRes));
        }
      }

      // Dispatch event so UI elements update immediately
      if (backendSuccess && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('libritui-unique-words-updated', {
          detail: { userId, language: langCode, total: newTotal }
        }));
        return newTotal;
      }
    } catch (err: any) {
      if (err?.message?.includes('SQLITE_CONSTRAINT') || err?.message?.includes('UNIQUE constraint failed')) {
        console.log('[uniqueWordsService] Unique word constraint handled gracefully.');
      } else {
        console.error('[uniqueWordsService] Erro ao gravar palavras únicas no D1:', err);
      }
    }

    return this.getUniqueWordsCount(userId, langCode);
  },

  /**
   * Fast SELECT on logs_unique_words for user_id and language.
   */
  async getUniqueWordsCount(userId: string | undefined, language?: string): Promise<number> {
    if (!userId || userId === 'guest') {
      try {
        const langCode = (language || 'en').toLowerCase();
        const localCacheKey = `libritui_unique_words_guest_${langCode}`;
        const stored = localStorage.getItem(localCacheKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          return parsed._words ? parsed._words.length : (parsed.total || 0);
        }
      } catch {}
      return 0;
    }

    try {
      const langCode = language ? language.toLowerCase() : 'all';

      if (langCode === 'all') {
        const rows = await dbManager.from('logs_unique_words', userId).select({
          user_id: userId
        });
        if (rows && Array.isArray(rows)) {
          return rows.reduce((sum: number, r: any) => sum + (r.total_unique_words || 0), 0);
        }
      } else {
        const rows = await dbManager.from('logs_unique_words', userId).select({
          user_id: userId,
          language: langCode
        });
        if (rows && rows.length > 0) {
          return rows[0].total_unique_words || 0;
        }
      }
    } catch (err) {
      console.error('[uniqueWordsService] Erro ao buscar contagem de palavras únicas:', err);
    }
    return 0;
  },

  /**
   * Fetches unique words count per language for statistics filter dropdown.
   */
  async getAllLanguageUniqueWords(userId: string | undefined): Promise<Record<string, number>> {
    const result: Record<string, number> = {};
    if (!userId || userId === 'guest') return result;

    try {
      const rows = await dbManager.from('logs_unique_words', userId).select({
        user_id: userId
      });
      if (rows && Array.isArray(rows)) {
        for (const row of rows) {
          if (row.language) {
            result[row.language.toLowerCase()] = row.total_unique_words || 0;
          }
        }
      }
    } catch (err) {
      console.error('[uniqueWordsService] Erro ao buscar palavras por idioma:', err);
    }
    return result;
  },

  /**
   * Fetches daily word stats (words learned per day) from logs_unique_words (completed_ids_json -> daily_words)
   * with fallback to user_daily_word_stats.
   */
  async getDailyWordStats(userId: string | undefined, language?: string): Promise<Record<string, number>> {
    const dailyMap: Record<string, number> = {};
    if (!userId || userId === 'guest') return dailyMap;

    try {
      const langCode = (language || 'all').toLowerCase();
      let rows: any[] = [];

      if (langCode === 'all') {
        rows = await dbManager.from('logs_unique_words', userId).select({ user_id: userId });
      } else {
        rows = await dbManager.from('logs_unique_words', userId).select({ user_id: userId, language: langCode });
      }

      if (rows && Array.isArray(rows) && rows.length > 0) {
        for (const row of rows) {
          if (row.completed_ids_json) {
            try {
              const parsed = typeof row.completed_ids_json === 'string' ? JSON.parse(row.completed_ids_json) : row.completed_ids_json;
              
              if (parsed && parsed.daily_words && typeof parsed.daily_words === 'object') {
                for (const [dateStr, wordsArr] of Object.entries(parsed.daily_words)) {
                  if (dateStr && Array.isArray(wordsArr)) {
                    dailyMap[dateStr] = (dailyMap[dateStr] || 0) + wordsArr.length;
                  }
                }
              } else if (parsed && Array.isArray(parsed.unique_words) && parsed.unique_words.length > 0) {
                // Legacy fallback: if daily_words object is not yet populated in completed_ids_json, fallback to updated_at date
                const rowDate = row.updated_at ? row.updated_at.split('T')[0] : new Date().toISOString().split('T')[0];
                dailyMap[rowDate] = (dailyMap[rowDate] || 0) + parsed.unique_words.length;
              }
            } catch (e) {
              console.warn('[uniqueWordsService] Error parsing completed_ids_json in getDailyWordStats:', e);
            }
          }
        }
      }

      // Fallback: If no entries found in logs_unique_words daily_words, check user_daily_word_stats
      if (Object.keys(dailyMap).length === 0) {
        let fallbackRows: any[] = [];
        if (langCode === 'all') {
          fallbackRows = await dbManager.from('user_daily_word_stats', userId).select({ user_id: userId });
        } else {
          fallbackRows = await dbManager.from('user_daily_word_stats', userId).select({ user_id: userId, language: langCode });
        }

        if (fallbackRows && Array.isArray(fallbackRows)) {
          for (const fRow of fallbackRows) {
            if (fRow.activity_date) {
              const dStr = fRow.activity_date;
              dailyMap[dStr] = (dailyMap[dStr] || 0) + (fRow.words_learned_today || fRow.words_learned || 0);
            }
          }
        }
      }
    } catch (err) {
      console.warn('[uniqueWordsService] Error fetching daily word stats from D1:', err);
    }
    return dailyMap;
  },

  /**
   * Fetches per-word frequency dictionary { [word: string]: count } for user & language.
   */
  async getWordFrequencies(userId: string | undefined, language?: string): Promise<Record<string, number>> {
    const freqMap: Record<string, number> = {};
    if (!userId || userId === 'guest') return freqMap;

    try {
      const langCode = (language || 'all').toLowerCase();
      let rows: any[] = [];

      if (langCode === 'all') {
        rows = await dbManager.from('logs_unique_words', userId).select({ user_id: userId });
      } else {
        rows = await dbManager.from('logs_unique_words', userId).select({ user_id: userId, language: langCode });
      }

      if (rows && Array.isArray(rows)) {
        for (const row of rows) {
          if (row.completed_ids_json) {
            try {
              const parsed = typeof row.completed_ids_json === 'string' ? JSON.parse(row.completed_ids_json) : row.completed_ids_json;
              if (parsed && parsed.word_counts && typeof parsed.word_counts === 'object') {
                for (const [w, count] of Object.entries(parsed.word_counts)) {
                  const cleanW = w.toLowerCase().trim();
                  freqMap[cleanW] = (freqMap[cleanW] || 0) + (Number(count) || 1);
                }
              } else if (parsed && Array.isArray(parsed.unique_words)) {
                // Fallback for unique words without word_counts map
                for (const w of parsed.unique_words) {
                  const cleanW = String(w).toLowerCase().trim();
                  freqMap[cleanW] = (freqMap[cleanW] || 0) + 1;
                }
              }
            } catch (e) {}
          }
        }
      }
    } catch (err) {
      console.warn('[uniqueWordsService] Error fetching word frequencies:', err);
    }
    return freqMap;
  },

  /**
   * Returns exact count of times a specific word was encountered by user.
   */
  async getWordSeenCount(userId: string | undefined, language: string, word: string): Promise<number> {
    if (!word) return 0;
    const freqs = await this.getWordFrequencies(userId, language);
    const cleanW = word.toLowerCase().trim();
    return freqs[cleanW] || 0;
  },

  /**
   * Retroactive sync for exercises completed before update
   */
  async syncPreviousExercises(userId: string | undefined, language: string): Promise<void> {
    if (!userId || userId === 'guest') return;
    try {
      const langCode = (language || 'en').toLowerCase();
      
      // 1. Sync roadmap completed topics
      const savedTopics = localStorage.getItem('libritui_roadmap_completed_topics');
      if (savedTopics) {
        const topics: string[] = JSON.parse(savedTopics);
        for (const topicId of topics) {
          await this.recordExerciseCompletion(userId, langCode, 'roadmap', topicId);
        }
      }

      // 2. Sync writing corrections
      const savedWriting = localStorage.getItem('libritui_writing_corrections_cache');
      if (savedWriting) {
        const writingItems: any[] = JSON.parse(savedWriting);
        for (const item of writingItems) {
          if (item.id || item.exerciseId) {
            await this.recordExerciseCompletion(userId, langCode, 'writing', item.id || item.exerciseId, item.original_text || item.user_input || '');
          }
        }
      }

      // 3. Sync pronunciation exercises
      const savedPron = localStorage.getItem('libritui_pronunciation_completed');
      if (savedPron) {
        const pronItems: any[] = JSON.parse(savedPron);
        for (const item of pronItems) {
          await this.recordExerciseCompletion(userId, langCode, 'pronunciation', item.id || item, item.text || '');
        }
      }
    } catch (err) {
      console.error('[uniqueWordsService] Erro ao sincronizar exercícios anteriores:', err);
    }
  }
};

export default uniqueWordsService;
